import type { MembershipApi } from "../../api/MembershipApi";
import type { CreateMembershipType } from "../../generated/dto/CreateMembershipType";
import type { DeleteMembershipTypeStaff } from "../../generated/dto/DeleteMembershipTypeStaff";
import type { MembershipMember } from "../../generated/dto/MembershipMember";
import type { MembershipMemberFetchOptions } from "../../generated/dto/MembershipMemberFetchOptions";
import type { MembershipMemberSearchResult } from "../../generated/dto/MembershipMemberSearchResult";
import type { MembershipSearchParams } from "../../generated/dto/MembershipSearchParams";
import type { MembershipStaff } from "../../generated/dto/MembershipStaff";
import type { MembershipStaffFetchOptions } from "../../generated/dto/MembershipStaffFetchOptions";
import type { MembershipStaffSearchResult } from "../../generated/dto/MembershipStaffSearchResult";
import type { MembershipTypeFetchOptions } from "../../generated/dto/MembershipTypeFetchOptions";
import type { MembershipTypeMember } from "../../generated/dto/MembershipTypeMember";
import type { MembershipTypeSearchParams } from "../../generated/dto/MembershipTypeSearchParams";
import type { MembershipTypeStaff } from "../../generated/dto/MembershipTypeStaff";
import type { MembershipTypeStaffSearchResult } from "../../generated/dto/MembershipTypeStaffSearchResult";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonMember } from "../../generated/dto/PersonMember";
import type { PersonMemberFetchOptions } from "../../generated/dto/PersonMemberFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { ProgramTypeStaff } from "../../generated/dto/ProgramTypeStaff";
import type { UpdateMembershipType } from "../../generated/dto/UpdateMembershipType";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class MembershipFrontendService {
  constructor(private readonly api: MembershipApi) {}
  private readonly membershipTypeStaffCache: Map<number, MembershipTypeStaff> = new Map();
  async getMembershipTypeStaff(id: number, options?: MembershipTypeFetchOptions): Promise<MembershipTypeStaff> {
    const __cached = this.membershipTypeStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getMembershipTypeStaff(id, options);
    this.membershipTypeStaffCache.set(id, __value);
    return __value
  }
  async getMembershipMember(id: number, options?: MembershipMemberFetchOptions): Promise<MembershipMember> {
    return this.api.getMembershipMember(id, options)
  }
  async getMembershipStaff(id: number, options?: MembershipStaffFetchOptions): Promise<MembershipStaff> {
    return this.api.getMembershipStaff(id, options)
  }
  async getAllMembershipTypeStaffs(options?: MembershipTypeFetchOptions): Promise<Array<MembershipTypeStaff>> {
    return this.api.getAllMembershipTypeStaffs(options)
  }
  async createMembershipTypeStaff(source: CreateMembershipType): Promise<MembershipTypeStaff> {
    const __result = await this.api.createMembershipTypeStaff(source);
    this.membershipTypeStaffCache.clear();
    return __result
  }
  async updateMembershipTypeStaff(source: UpdateMembershipType): Promise<MembershipTypeStaff> {
    const __result = await this.api.updateMembershipTypeStaff(source);
    this.membershipTypeStaffCache.clear();
    return __result
  }
  async deleteMembershipTypeStaff(source: DeleteMembershipTypeStaff): Promise<void> {
    const __result = await this.api.deleteMembershipTypeStaff(source);
    this.membershipTypeStaffCache.clear();
    return __result
  }
  async searchMembershipTypeStaff(params: MembershipTypeSearchParams, page: number, perPage: number): Promise<PaginationResult<MembershipTypeStaffSearchResult>> {
    return this.api.searchMembershipTypeStaff(params, page, perPage)
  }
  async searchMembershipMember(params: MembershipSearchParams, page: number, perPage: number): Promise<PaginationResult<MembershipMemberSearchResult>> {
    return this.api.searchMembershipMember(params, page, perPage)
  }
  async searchMembershipStaff(params: MembershipSearchParams, page: number, perPage: number): Promise<PaginationResult<MembershipStaffSearchResult>> {
    return this.api.searchMembershipStaff(params, page, perPage)
  }
  async searchMembershipTypeStaffFull(params: MembershipTypeSearchParams, page: number, perPage: number, options?: MembershipTypeFetchOptions): Promise<PaginationResult<MembershipTypeStaff>> {
    return this.api.searchMembershipTypeStaffFull(params, page, perPage, options)
  }
  async searchMembershipMemberFull(params: MembershipSearchParams, page: number, perPage: number, options?: MembershipMemberFetchOptions): Promise<PaginationResult<MembershipMember>> {
    return this.api.searchMembershipMemberFull(params, page, perPage, options)
  }
  async searchMembershipStaffFull(params: MembershipSearchParams, page: number, perPage: number, options?: MembershipStaffFetchOptions): Promise<PaginationResult<MembershipStaff>> {
    return this.api.searchMembershipStaffFull(params, page, perPage, options)
  }
  async getMembershipTypeStaffProgramId(id: number): Promise<ProgramTypeStaff | null> {
    return this.api.getMembershipTypeStaffProgramId(id)
  }
  async getMembershipMemberPersonId(id: number, options?: PersonMemberFetchOptions): Promise<PersonMember> {
    return this.api.getMembershipMemberPersonId(id, options)
  }
  async getMembershipStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getMembershipStaffPersonId(id, options)
  }
  async getMembershipStaffMembershipTypeId(id: number, options?: MembershipTypeFetchOptions): Promise<MembershipTypeStaff> {
    return this.api.getMembershipStaffMembershipTypeId(id, options)
  }
  async purchaseOptionsMember(): Promise<Array<MembershipTypeMember>> {
    return this.api.purchaseOptionsMember()
  }
  async purchaseOptionsPublic(): Promise<Array<MembershipTypeMember>> {
    return this.api.purchaseOptionsPublic()
  }
}
